import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMapPin, FiCreditCard, FiPhone } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import SearchBar from '../components/SearchBar';
import ServiceGrid from '../components/ServiceGrid';
import MartSection from '../components/MartSection';
import LocationPicker from '../components/LocationPicker';
import { formatPrice } from '../utils/constants';
import './Home.css';

const Home = () => {
  const { user, cart, cartTotal } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showLocation, setShowLocation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [photoPack8Price, setPhotoPack8Price] = useState(40);

  useEffect(() => {
    fetchData();
    if (!user?.address?.lat) {
      setShowLocation(true);
    }
  }, [user?.address?.lat]);

  const fetchData = async () => {
    try {
      const [servicesRes, categoriesRes, ratesRes] = await Promise.all([
        API.get('/print-services'),
        API.get('/products/categories/all'),
        API.get('/print-rates').catch(() => ({ data: { success: false } }))
      ]);
      setServices(servicesRes.data.services || []);
      setCategories(categoriesRes.data.categories || []);
      if (ratesRes.data?.success && ratesRes.data?.rates) {
        const rate = ratesRes.data.rates.find(r => r.key === 'photo_pack8')?.value;
        if (rate) setPhotoPack8Price(rate);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayAddress = () => {
    const activeSaved = (user?.savedAddresses || []).find(sa => {
      if (user?.address?.lat && user?.address?.lng && sa.lat && sa.lng) {
        return Math.abs(sa.lat - user.address.lat) < 0.0001 && Math.abs(sa.lng - user.address.lng) < 0.0001;
      }
      if (user?.address?.line1 && sa.line1) {
        return sa.line1.trim().toLowerCase() === user.address.line1.trim().toLowerCase();
      }
      return false;
    }) || (user?.savedAddresses && user.savedAddresses.length > 0 ? user.savedAddresses[0] : null);

    const l1 = (user?.address?.line1 || activeSaved?.line1 || '').trim();
    const lbl = (user?.address?.label || activeSaved?.label || '').trim();
    const fl = (user?.address?.full || activeSaved?.full || '').trim();
    const receiver = (user?.address?.receiverName || activeSaved?.receiverName || (user?.name && user.name.toLowerCase() !== 'user' ? user.name : '')).trim();

    if (l1) {
      const isGeneric = lbl && ['home', 'work', 'office', 'other'].includes(lbl.toLowerCase());
      if (lbl && isGeneric && lbl.toLowerCase() !== l1.toLowerCase()) {
        const withReceiver = receiver ? ` (${receiver})` : '';
        return `${lbl} - ${l1}${withReceiver}`;
      }
      const withReceiver = receiver ? ` • ${receiver}` : '';
      return `${l1}${withReceiver}`;
    }
    if (fl && fl.toLowerCase() !== 'home') {
      const withReceiver = receiver ? ` • ${receiver}` : '';
      return `${fl}${withReceiver}`;
    }
    if (lbl) {
      const withReceiver = receiver ? ` • ${receiver}` : '';
      return `${lbl}${withReceiver}`;
    }
    return 'Set Location';
  };

  return (
    <div className="page home-page">
      {/* Header */}
      <div className="home-header">
        <div className="home-header-left">
          <button className="location-btn" onClick={() => setShowLocation(true)}>
            <FiMapPin className="location-icon" />
            <div className="location-text">
              <span className="location-label">Delivering to</span>
              <span className="location-address">
                {getDisplayAddress()}
              </span>
            </div>
          </button>
        </div>
        <div className="home-header-right">
          <button className="wallet-btn" onClick={() => navigate('/wallet')}>
            <FiCreditCard />
            <span>{formatPrice(user?.walletBalance || 0)}</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="home-search">
        <SearchBar />
      </div>

      {/* ── Service Banners ── */}
      {/* 1. Get it Printed – full-width hero */}
      <div className="home-banner home-banner--print" onClick={() => navigate('/print-store')}>
        <div className="banner-content">
          <h2>Get it Printed</h2>
          <p>Documents delivered under 30 mins ⚡</p>
        </div>
        <div className="banner-img-wrap">
           <img src="/images/printer.svg" alt="Printer" className="banner-img" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} />
          <span className="banner-img-fallback" style={{ display: 'none', fontSize: 48 }}>🖨️</span>
        </div>
      </div>

      {/* 2. PVC Card Printing – full-width banner */}
      <div className="home-banner home-banner--pvc" onClick={() => navigate('/pvc-card')}>
        <div className="banner-content">
          <h2>PVC Card Printing</h2>
          <p>ID cards, lost card affidavits &amp; more</p>
        </div>
        <div className="banner-img-wrap">
          <img src="/images/pvc-card.svg" alt="PVC Card" className="banner-img" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} />
          <span className="banner-img-fallback" style={{ display: 'none', fontSize: 48 }}>🪪</span>
        </div>
      </div>

      {/* 2b. NCR Prints - Substrate & UV Flatbed Quotation Studio */}
      <div
        className="home-banner home-banner--ncr"
        onClick={() => window.dispatchEvent(new CustomEvent('open-print-quote'))}
        style={{
          background: 'linear-gradient(135deg, #1b1638 0%, #251842 50%, #152238 100%)',
          border: '1px solid rgba(255, 183, 77, 0.35)',
          cursor: 'pointer'
        }}
      >
        <div className="banner-content">
          <div style={{ display: 'inline-block', background: 'rgba(255, 183, 77, 0.2)', color: '#FFB74D', fontSize: '0.65rem', fontWeight: 800, padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase', marginBottom: '4px' }}>
            Instant UV Quotation Studio
          </div>
          <h2 style={{ color: '#ffffff' }}>UV DTF, Acrylic &amp; Glass</h2>
          <p style={{ color: '#c4c8e2' }}>Instant live sq.in rates &amp; WhatsApp quotation ➔</p>
        </div>
        <div className="banner-img-wrap">
          <span style={{ fontSize: 44 }}>✨</span>
        </div>
      </div>

      {/* 3 & 4 – Side-by-side smaller cards */}
      <div className="home-mini-banners">
        {/* Print Photos */}
        <div className="mini-banner mini-banner--passport" onClick={() => navigate('/passport-photos')}>
          <div className="mini-banner-text">
            <span className="mini-banner-title">Print Photos</span>
            <span className="mini-banner-sub">Pro quality · ₹{photoPack8Price}/8 pics</span>
          </div>
          <div className="mini-banner-img-wrap mini-banner-img-wrap--passport">
             <img src="/images/passport-photo.svg" alt="Print Photos" className="mini-banner-img" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} />
            <span className="mini-banner-img-fallback" style={{ display: 'none', fontSize: 36 }}>📸</span>
          </div>
        </div>

        {/* On-Demand Printing */}
        <div className="mini-banner mini-banner--ondemand" onClick={() => navigate('/on-demand-print')}>
          <div className="mini-banner-text">
            <span className="mini-banner-title">Bulk / Custom</span>
            <span className="mini-banner-sub">Call or drop a request</span>
          </div>
          <div className="mini-banner-icon-wrap">
            <FiPhone size={32} className="mini-banner-phone-icon" />
          </div>
        </div>

      </div>

      {loading ? (
        <div className="spinner"></div>
      ) : (
        <>
          <ServiceGrid services={services} />
          <MartSection categories={categories} />
        </>
      )}

      {/* Floating Cart Bar */}
      {cart.length > 0 && (
        <div className="floating-cart" onClick={() => navigate('/checkout')}>
          <div className="floating-cart-info">
            <span className="floating-cart-count">{cart.length} item(s)</span>
            <span className="floating-cart-total">{formatPrice(cartTotal)}</span>
          </div>
          <span className="floating-cart-action">Checkout →</span>
        </div>
      )}

      {showLocation && <LocationPicker onClose={() => setShowLocation(false)} />}

      {/* Android Live App Download Banner Card */}
      <section className="home-app-card">
        <div className="home-app-card-info">
          <span className="home-app-badge">Official Android App</span>
          <h3 className="home-app-title">Experience Faster Prints on Mobile</h3>
          <p className="home-app-desc">
            Install the Shubink Live app on Android for real-time order tracking, live status vibrations, and express 30-min delivery in Jhajjar.
          </p>
          <div className="home-app-cta-row">
            <a
              href="https://play.google.com/store/apps/details?id=com.shubhamcomputers.shubink"
              target="_blank"
              rel="noopener noreferrer"
              className="home-app-play-btn"
            >
              <span>Get it on Google Play</span>
            </a>
          </div>
        </div>
        <div className="home-app-qr-box">
          <img
            src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dcom.shubhamcomputers.shubink&margin=6"
            alt="Scan QR code to install Shubink app"
            className="home-app-qr-img"
            width="120"
            height="120"
            loading="lazy"
          />
          <span className="home-app-qr-text">Scan with camera</span>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="home-footer-brand">
          <span className="home-footer-logo">🖨️</span>
          <div>
            <h4 className="home-footer-name">Shubham Computers</h4>
            <p className="home-footer-tagline">Your local print &amp; stationery mart</p>
          </div>
        </div>

        <div className="home-footer-links">
          <div className="home-footer-col">
            <span className="home-footer-col-title">Services</span>
            <span>Document Printing</span>
            <span>Print Photos</span>
            <span>Bulk &amp; Custom Print</span>
            <span>Stationery Mart</span>
          </div>
          <div className="home-footer-col">
            <span className="home-footer-col-title">Contact</span>
            <a className="home-footer-link" href="tel:+919350336367">+91 93503 36367</a>
            <span>Jhajjar, Haryana</span>
            <span>Mon – Sat · 9 AM – 8 PM</span>
          </div>
        </div>

        <div className="home-footer-bottom">
          <span>© {new Date().getFullYear()} Shubham Computers. All rights reserved.</span>
        </div>
      </footer>

      <div className="fab-spacer"></div>
    </div>
  );
};

export default Home;
